import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import backend from '@api/backend'
import { ICommentsPublicRaw, IComments, NewAlertCommentData, UpdateAlertCommentData } from '../model/comments.model'
import { notifySuccess } from '@common/utils/notify'
import { DeviceIDTypes } from '@data/device/model/device'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { useAuthStore } from '@store/auth.store'

// Exported so the alert data layer can invalidate a group's comments alongside the group
// itself, without repeating the literal.
export const alertCommentsQueryKey = (
  deviceId: string | undefined,
  alertGroupId: string | undefined,
) => ['alert-comments', deviceId, alertGroupId] as const

export const useAlertComments = (deviceId: string | undefined, alertId: string | undefined) => {
  // The API scopes GET /usage_alerts/{device_id}/comment on UPDATE:ALERTS:USAGE — the same
  // scope that governs acting on an alert. Without it the request is a guaranteed 403, so it
  // is never sent: every alert rendered in the queue or the historical view would otherwise
  // fire one.
  const canReadAlertComments = useAuthStore((state) =>
    hasPermissions(state.permissions, ['UPDATE:ALERTS:USAGE']),
  )

  return useQuery({
    queryKey: alertCommentsQueryKey(deviceId, alertId),
    queryFn: () => getAlertComments(deviceId, alertId, DeviceIDTypes.dUuid),
    enabled: !!deviceId && !!alertId && canReadAlertComments,
  })
}

export const useAlertCommentsCreation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postAlertComment,
    onSuccess: (data: ICommentsPublicRaw, { device_id, alertGroupId }) => {
      const newComment = {
        id: data.id,
        comment: data.comment,
        createdAt: data.date,
        updatedAt: data.validFrom,
        author: data.userName,
        history: [],
      }

      const updatedComments = queryClient.setQueryData(
        alertCommentsQueryKey(device_id, alertGroupId),
        (old: IComments[]) => {
          return [newComment, ...old]
        },
      )

      notifySuccess('Comment created successfully')

      return updatedComments
    },
  })
}

export const useAlertCommentsUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateAlertComment,
    onSuccess: (data: ICommentsPublicRaw, { device_id, alertGroupId }) => {
      const updatedComment = {
        id: data.id,
        comment: data.comment,
        createdAt: data.date,
        updatedAt: data.validFrom,
        author: data.userName,
        history: data.history?.map((h) => ({
          comment: h.comment,
          author: h.userName,
          createdAt: h.date,
          updatedAt: data.validTo,
        })),
      }

      const updatedComments = queryClient.setQueryData(
        alertCommentsQueryKey(device_id, alertGroupId),
        (oldComments: IComments[]) => {
          return oldComments.map((comment: IComments) =>
            comment.id === updatedComment.id ? updatedComment : comment,
          )
        },
      )

      notifySuccess('Comment updated successfully')

      return updatedComments
    },
  })
}

const postAlertComment = async (data: NewAlertCommentData) => {
  const response = await backend.post(
    `usage_alerts/${data.device_id}/comment`, data, {
      params : { idType: DeviceIDTypes.dUuid }
    }
  )
  return response.data
}

const updateAlertComment = async (data: UpdateAlertCommentData) => {
  const response = await backend.patch(
    `usage_alerts/${data.device_id}/comments`, data, {
      params: {
        idType: DeviceIDTypes.dUuid
      }
    }
  )

  return response.data
}

export async function getAlertComments(deviceId: string | undefined, alertGroupId: string | undefined, idType: DeviceIDTypes) {
  const comments = await backend.get(`/usage_alerts/${deviceId}/comment`, {
    params: {
      alertGroupId,
      idType,
    },
  })

  comments.data.sort((a: ICommentsPublicRaw, b: ICommentsPublicRaw) => b.id - a.id)

  const alertComments: IComments[] = comments.data.map((comment: ICommentsPublicRaw) => {
    return {
      id: comment.id,
      comment: comment.comment,
      createdAt: comment.date,
      updatedAt: comment.validFrom,
      author: comment.userName,
      history: comment.history?.map((history: ICommentsPublicRaw) => {
        return {
          id: history.id,
          comment: history.comment,
          author: history.userName,
          createdAt: history.validFrom,
        }
      }),
    }
  })
  return alertComments
}
